import { usePolling } from '@/hooks/usePolling';
import type { UsePollingReturn } from '@/hooks/usePolling';
import { searchResultCardStatusGet } from '@/services/swagger/easourcing/SessionService';
import type { IApiSearchResultCardStatusResp } from '@/services/swagger/easourcing/models';

export type UseSearchCardPollingOptions = {
  /** 外层消息 ID，用于传递给轮询接口 */
  messageId: string | number;
  /** 当前会话 ID */
  sessionId: string | number;
  /** submessage ID，轮询目标标识 */
  subMessageId: string;
  /**
   * 轮询完成回调（status=1）。
   * 参数为接口返回的完整状态响应，调用方从中取 cardData 更新 store。
   */
  onComplete: (resp: IApiSearchResultCardStatusResp) => void;
  /** 轮询出错回调（连续错误超限或超时），参数为错误描述文案 */
  onError?: (message: string) => void;
};

export type UseSearchCardPollingReturn = UsePollingReturn;

/**
 * useSearchCardPolling
 *
 * 针对单个 SearchCardV2 卡片的轮询 Hook。
 * 基于通用 `usePolling` 实现，负责：
 * 1. 每 2s 调用 searchResultCardStatusGet 查询搜索结果状态
 * 2. status=1（已完成）时调用 onComplete 回调，停止轮询
 * 3. 连续错误 ≥ 3 时调用 onError 并停止
 * 4. 超过 150 次时视为超时，调用 onError 并停止
 * 5. 组件卸载时自动清理定时器
 */
export function useSearchCardPolling({
  messageId,
  sessionId,
  subMessageId,
  onComplete,
  onError,
}: UseSearchCardPollingOptions): UseSearchCardPollingReturn {
  return usePolling<IApiSearchResultCardStatusResp>({
    fetchFn: () =>
      searchResultCardStatusGet(
        {
          messageId: Number(messageId),
          sessionId: Number(sessionId),
          subMessageId,
        },
        { skipGlobalErrorHandling: true } as Record<string, unknown>
      ).then(result => result?.data as IApiSearchResultCardStatusResp),
    isComplete: data => data?.cardData?.status === 1,
    onComplete,
    onError,
    timeoutMessage: 'Search timed out. Please try again.',
    errorMessage: 'Search failed after multiple retries. Please try again.',
  });
}
